// import { useEffect } from "react";
// import DOMPurify from "dompurify";
// import type { Ad } from "../AdvertisementSection";
// import { X } from "lucide-react";

// type Props = {
//   ad: Ad | null;
//   open: boolean;
//   onClose: () => void;
// };

// export function AdPopUp({ ad, open, onClose }: Props) {
//   // close on ESC key
//   useEffect(() => {
//     const handler = (e: KeyboardEvent) => {
//       if (e.key === "Escape") onClose();
//     };
//     if (open) window.addEventListener("keydown", handler);
//     return () => window.removeEventListener("keydown", handler);
//   }, [open, onClose]);

//   if (!open || !ad) return null;

//   return (
//     <div
//       className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
//       onClick={onClose}
//     >
//       {/* modal */}
//       <div
//         className="w-full max-w-2xl bg-[#0f172a] border border-white/10 rounded-2xl shadow-2xl"
//         onClick={(e) => e.stopPropagation()}
//       >
//         {/* header */}
//         <div className="flex items-start justify-between p-5 border-b border-white/10">
//           <h2 className="text-lg md:text-xl font-semibold text-white">
//             {ad?.title ?? ""}
//           </h2>

//           <button
//             onClick={onClose}
//             className="text-slate-400 hover:text-white text-xl leading-none cursor-pointer"
//           >
//             <X />
//           </button>
//         </div>

//         {/* body */}
//         <div className="p-5 max-h-[70vh] overflow-y-auto space-y-4">
//           {ad?.image && (
//             <img
//               src={ad?.image}
//               alt={ad?.title}
//               className="w-full h-56 object-cover rounded-lg"
//             />
//           )}

//           {/* rich text description */}
//           <div
//             className="prose prose-invert max-w-none prose-p:leading-relaxed"
//             dangerouslySetInnerHTML={{
//               __html: DOMPurify.sanitize(ad?.description ?? ""),
//             }}
//           />
//         </div>

//         {/* footer */}
//         <div className="flex items-center justify-end gap-3 p-5 border-t border-white/10">
//           <button
//             onClick={onClose}
//             className="px-4 py-2 rounded-lg border border-white/15 text-slate-300 hover:bg-white/5 text-sm"
//           >
//             Close
//           </button>

//           {ad.url && (
//             <a 
//               href={ad.url} 
//               target="_blank" 
//               rel="noopener noreferrer"
//               className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium"
//             >
//                 Learn More
//             </a>
//           )}

//         </div>
//       </div>
//     </div>
//   );
// }

import { useEffect } from "react";
import DOMPurify from "dompurify";
import type { Ad } from "../AdvertisementSection";
import { X } from "lucide-react";

type Props = {
  ad: Ad | null;
  open: boolean;
  onClose: () => void;
};

export function AdPopUp({ ad, open, onClose }: Props) {

  // ESC close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // 🔒 lock background scroll
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open || !ad) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl max-h-[90vh] bg-[#0f172a] border border-white/10 rounded-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="sticky top-0 flex justify-between p-6 border-b border-white/10 bg-[#0f172a] rounded-t-2xl">
          <h2 className="text-xl md:text-2xl font-semibold text-white pr-4">
            {ad?.title ?? ""}
          </h2>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white"
          >
            <X />
          </button>
        </div>

        {/* body */}
        <div className="overflow-y-auto p-6 space-y-3 hide-scrollbar">
          {ad?.image && (
            <div className="relative w-full aspect-video rounded-xl border border-white/10 overflow-hidden">
              {/* soft blurred background */}
              <img
                src={ad?.image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover blur-md scale-105 opacity-25"
              />

              {/* main image */}
              <div className="relative w-full h-full flex items-center justify-center bg-black/20">
                <img
                  src={ad?.image}
                  alt={ad?.title ?? ""}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            // <div className="w-full aspect-video bg-black/40 rounded-xl border border-white/10 overflow-hidden flex items-center justify-center">
            //   <img
            //     src={ad?.image}
            //     alt={ad?.title ?? ""}
            //     className="w-full h-full object-fill"
            //   />
            // </div>
          )}

          <div
            className="ad-rich-text"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(ad?.description ?? ""),
            }}
          />
        </div>

        {/* footer */}
        <div className="sticky bottom-0 flex justify-end gap-3 p-5 border-t border-white/10 bg-[#0f172a] rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-white/15 text-slate-300 hover:bg-white/5 text-sm"
          >
            Close
          </button>

          {ad?.url && (
            <a
              href={ad?.url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium"
            >
              Learn More
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

